import React from "react";
import { StyleSheet, View } from "react-native";
import { StackSimpleIcon, ShieldIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { ActivityDetail, StructuredFields } from "../types/reviewTypes";
import type { CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";
import ActivityPanelTabs from "./ActivityPanelTabs";
import RecommendationSourceList from "./RecommendationSourceList";
import SegmentedDiffView from "./SegmentedDiffView";

interface ModuleDescriptionPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetail: ActivityDetail | null;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFields) => void;
  sourceLabel: string;
}

function ModuleDescriptionPanel({
  selectedReviewItem,
  activityDetail,
  isEditDisabled,
  onSaveEdit,
  sourceLabel,
}: ModuleDescriptionPanelProps) {
  const detail = activityDetail;
  const hasRecommendationSources = Boolean(detail?.recommendationSources?.length);
  const shouldShowFeedback = Boolean(detail?.feedback && !hasRecommendationSources);
  const moduleDescriptionsSupported = sourceLabel !== "Canvas";

  const currentFields: StructuredFields = {
    title: detail?.currentTitle ?? selectedReviewItem.title,
    description: detail?.currentDescription ?? "",
  };

  const proposedFields: StructuredFields = {
    title: detail?.recommendedTitle ?? selectedReviewItem.title,
    description: detail?.recommendedDescription ?? "",
  };

  return (
    <View style={styles.panel}>
      {/* Type badge */}
      <View style={styles.typeBadge}>
        <StackSimpleIcon size={14} color="#7C3AED" weight="bold" />
        <CustomText
          text="Module Description"
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.typeBadgeText}
        />
      </View>

      {/* Title */}
      <CustomText
        text={selectedReviewItem.title}
        useTranslationText={false}
        size="xl"
        weight="bold"
        style={styles.title}
      />

      <ActivityPanelTabs attachments={detail?.attachments}>
        <View style={styles.reviewContent}>
          {!moduleDescriptionsSupported && (
            <View style={styles.lmsLimitationNote}>
              <CustomText
                text={`${sourceLabel} doesn't support module descriptions yet — only the title is editable here.`}
                useTranslationText={false}
                size="xs"
                style={styles.lmsLimitationText}
              />
            </View>
          )}

          {/* Why card */}
          {(shouldShowFeedback || hasRecommendationSources) && (
            <View style={styles.whyCard}>
              <CustomText
                text="Why we're recommending changes"
                useTranslationText={false}
                size="s"
                weight="bold"
                style={styles.whyHeading}
              />
              {shouldShowFeedback && (
                <CustomText text={detail.feedback} useTranslationText={false} size="xs" style={styles.whyBody} />
              )}
              <RecommendationSourceList recommendations={detail?.recommendationSources} />
              {detail?.rubricCode && (
                <View style={styles.rubricRow}>
                  <ShieldIcon size={12} color={colors.brand[600]} weight="fill" />
                  <CustomText
                    text={`${detail.rubricCode}: ${detail.rubricTitle}`}
                    useTranslationText={false}
                    size="xs"
                    weight="medium"
                    style={styles.rubricLink}
                  />
                  {detail.rubricDescription && (
                    <CustomText
                      text={` \u2014 ${detail.rubricDescription}`}
                      useTranslationText={false}
                      size="xs"
                      style={styles.rubricDescription}
                    />
                  )}
                </View>
              )}
            </View>
          )}

          {/* Segmented diff view */}
          <SegmentedDiffView
            variant="module"
            currentFields={currentFields}
            proposedFields={proposedFields}
            isEditDisabled={isEditDisabled}
            onSaveEdit={onSaveEdit}
          />

          {/* Last modified */}
          {detail?.lastModifiedBy && (
            <CustomText
              text={`Last modified by ${detail.lastModifiedBy} on ${detail.lastModifiedAt ?? "unknown date"}`}
              useTranslationText={false}
              size="xs"
              style={styles.lastModified}
            />
          )}
        </View>
      </ActivityPanelTabs>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  reviewContent: {
    gap: 16,
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    alignSelf: "flex-start",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: "#F3EEFF",
  },
  typeBadgeText: {
    color: "#7C3AED",
  },
  title: {
    color: colors.grey[900],
  },
  whyCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 6,
  },
  whyHeading: {
    color: colors.grey[900],
    marginBottom: 2,
  },
  whyBody: {
    color: colors.grey[600],
    lineHeight: 20,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  rubricLink: {
    color: colors.brand[600],
  },
  rubricDescription: {
    color: colors.grey[500],
  },
  lastModified: {
    color: colors.grey[400],
  },
  lmsLimitationNote: {
    backgroundColor: colors.warning[100],
    borderWidth: 1,
    borderColor: colors.warning[300],
    borderRadius: 12,
    padding: 12,
  },
  lmsLimitationText: {
    color: colors.warning[800],
    lineHeight: 18,
  },
});

export default ModuleDescriptionPanel;
